import { type Metrics } from '@atlassian/metrics-interface';
import { type Recipient } from '@post-office/shared-contracts/recipients';

const instrumentedStages = ['data-retrieval', 'filtering', 'hydration'] as const;

type InstrumentedStage = (typeof instrumentedStages)[number];

const PULL_MESSAGE_PERFORMANCE_PER_PLACEMENT = 'message-performance.pull.per-placement';
const PUSH_MESSAGE_PERFORMANCE_PER_RECIPIENT_TYPE = 'message-performance.push.per-recipient-type';

export const PULL_HISTOGRAM_LATENCY_INTERVAL_BUCKETS = [
	10, 25, 50, 100, 150, 200, 250, 300, 400, 500,
];
export const PUSH_HISTOGRAM_LATENCY_INTERVAL_BUCKETS = [100, 500, 1000];

type MessagePerformanceMetricTags = {
	messageTemplateId: string;
	operation: string;
};

type PullChannelMessagePerformanceMetricNameSegments = {
	flow: 'pull';
	placementId: string;
	stage: InstrumentedStage;
};

type PushChannelMessagePerformanceMetricNameSegments = {
	flow: 'push';
	recipientType: Recipient['type'];
	stage: InstrumentedStage;
};

export class MessagePerformance {
	constructor(
		private readonly metrics: Metrics,
		private readonly metricNameSegments:
			| PullChannelMessagePerformanceMetricNameSegments
			| PushChannelMessagePerformanceMetricNameSegments,
	) {}

	async measureOperation<T>(
		callback: () => Promise<T>,
		tags: MessagePerformanceMetricTags,
	): Promise<T> {
		// Metrics are namespaced:
		// * by placementId and stage for pull flow
		// * by recipientType and stage for push flow
		// to reduce cardinality;
		const metricName =
			this.metricNameSegments.flow === 'pull'
				? `${PULL_MESSAGE_PERFORMANCE_PER_PLACEMENT}.${this.metricNameSegments.placementId}.${this.metricNameSegments.stage}`
				: `${PUSH_MESSAGE_PERFORMANCE_PER_RECIPIENT_TYPE}.${this.metricNameSegments.recipientType}.${this.metricNameSegments.stage}`;

		const HISTOGRAM_LATENCY_INTERVAL_BUCKETS =
			this.metricNameSegments.flow === 'pull'
				? PULL_HISTOGRAM_LATENCY_INTERVAL_BUCKETS
				: PUSH_HISTOGRAM_LATENCY_INTERVAL_BUCKETS;

		// `messageTemplateId` and `operation` are added as tags to allow building charts comparing performance
		// of different message templates and observe performance problems with individual operations.
		const timer = this.metrics
			.histogram(metricName, HISTOGRAM_LATENCY_INTERVAL_BUCKETS, tags)
			.measure();
		let status: 'success' | 'failure' = 'success';

		try {
			return await callback();
		} catch (error) {
			status = 'failure';
			throw error;
		} finally {
			timer.stop({ status });
		}
	}
}
